import Papa from 'papaparse'

export const BULK_REJECT_MAX_ROWS = 500

export const BULK_REJECT_MAX_CSV_SIZE_BYTES = 2 * 1024 * 1024

export const BULK_REJECT_REQUIRED_HEADERS = ['agentVerificationId', 'rejectionReasonCode'] as const

const FRAUDULENT_FLAGS_SEPARATOR = ';'

export type ParsedBulkRejectRow = {
  agentVerificationId: string
  rejectionReasonCode: string
  fraudulentFlags: string[]
}

export type ParsedBulkRejectCsv = {
  rows: ParsedBulkRejectRow[]
  missingHeaders: string[]
  reasonCounts: { rejectionReasonCode: string; count: number }[]
  duplicateIds: string[]
}

const emptyResult: ParsedBulkRejectCsv = { rows: [], missingHeaders: [], reasonCounts: [], duplicateIds: [] }

export function parseBulkRejectCsv(csvText: string): ParsedBulkRejectCsv {
  const { data, meta } = Papa.parse<Record<string, string>>(csvText, {
    header: true,
    skipEmptyLines: true,
    transformHeader: header => header.trim()
  })

  const headers = meta.fields ?? []
  const missingHeaders = BULK_REJECT_REQUIRED_HEADERS.filter(header => !headers.includes(header))

  if (missingHeaders.length > 0) return { ...emptyResult, missingHeaders }

  const rows = data
    .map(row => ({
      agentVerificationId: row.agentVerificationId?.trim() ?? '',
      rejectionReasonCode: row.rejectionReasonCode?.trim() ?? '',
      fraudulentFlags: (row.fraudulentFlags ?? '')
        .split(FRAUDULENT_FLAGS_SEPARATOR)
        .map(flag => flag.trim())
        .filter(Boolean)
    }))
    .filter(row => row.agentVerificationId !== '' || row.rejectionReasonCode !== '')

  const countsByReason = rows.reduce<Record<string, number>>((acc, { rejectionReasonCode }) => {
    acc[rejectionReasonCode] = (acc[rejectionReasonCode] ?? 0) + 1

    return acc
  }, {})

  const seen = new Set<string>()
  const duplicateIds = new Set<string>()

  rows.forEach(({ agentVerificationId }) => {
    if (seen.has(agentVerificationId)) {
      duplicateIds.add(agentVerificationId)

      return
    }

    seen.add(agentVerificationId)
  })

  return {
    rows,
    missingHeaders: [],
    reasonCounts: Object.entries(countsByReason)
      .map(([rejectionReasonCode, count]) => ({ rejectionReasonCode, count }))
      .sort((a, b) => b.count - a.count),
    duplicateIds: [...duplicateIds]
  }
}

export const buildBulkRejectTemplateCsv = () =>
  Papa.unparse({
    fields: ['agentVerificationId', 'rejectionReasonCode', 'fraudulentFlags'],
    data: [
      ['agentverification_000000000000000000000000000', 'invalid_documentation', ''],
      [
        'agentverification_111111111111111111111111111',
        'suspicious_application',
        'agent_reused_email;agent_suspicious_email'
      ]
    ]
  })
